// © 2026 Buddy Media LLC. All rights reserved.
import { useEffect, useMemo, useState } from "react";

import { AdminCard } from "@/components/AdminCard";
import { AdminPageShell } from "@/components/AdminPageShell";
import { apiFetch, describeApiError } from "@/lib/api";

// T-019 — service-account admin surface.
// Marc-greenlit auth-posture change: each AI worker gets its own
// long-lived MCP bearer minted here. Stored in env var on the
// worker. Revocable per-account or per-bearer. The bearer value
// is shown ONCE on mint — never readable again.
//
// Backed by /api/admin/service-accounts/. See
// docs/MCP_CLIENT_SETUP.md Flow D for the consumer-side
// procedure.

type ServiceAccount = {
  id: string;
  name: string;
  description: string | null;
  user_id: number;
  is_active: boolean;
  created_at: string;
  created_by_user_id: number | null;
  bearer_count: number;
  last_bearer_used_at: string | null;
};

type Bearer = {
  id: string;
  jti: string;
  name: string | null;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
  created_by_user_id: number | null;
};

type ServiceAccountDetail = ServiceAccount & { bearers: Bearer[] };

type MintResponse = {
  bearer: string;
  jti: string;
  name: string | null;
  expires_at: string;
  service_account_id: string;
  notice: string;
};

export function ServiceAccountsPage() {
  const [accounts, setAccounts] = useState<ServiceAccount[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newTtl, setNewTtl] = useState<number | "">("");
  const [newlyMinted, setNewlyMinted] = useState<MintResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    apiFetch<ServiceAccount[]>("/api/admin/service-accounts")
      .then((data) => {
        if (!cancelled) setAccounts(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(describeApiError(err, "Failed to load service accounts"));
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const submitCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await apiFetch<ServiceAccount>("/api/admin/service-accounts", {
        method: "POST",
        body: JSON.stringify({
          name: newName.trim(),
          description: newDesc.trim() || null,
        }),
      });
      setNewName("");
      setNewDesc("");
      refresh();
    } catch (err) {
      setError(describeApiError(err, "Create failed"));
    } finally {
      setCreating(false);
    }
  };

  // Quick mint — single click that creates the SA AND mints a
  // default bearer, surfacing the token in NewlyMintedBanner.
  // Replaces the "create, click in, click mint, copy" multi-step
  // flow when you just want a usable bearer for a worker/agent.
  const submitQuickMint = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const sa = await apiFetch<ServiceAccount>(
        "/api/admin/service-accounts",
        {
          method: "POST",
          body: JSON.stringify({
            name: newName.trim(),
            description: newDesc.trim() || null,
          }),
        },
      );
      const mint = await apiFetch<MintResponse>(
        `/api/admin/service-accounts/${sa.id}/bearers`,
        {
          method: "POST",
          body: JSON.stringify({
            name: `${sa.name}-default`,
            ttl_days: typeof newTtl === "number" ? newTtl : null,
          }),
        },
      );
      setNewName("");
      setNewDesc("");
      setNewTtl("");
      setNewlyMinted(mint);
      refresh();
    } catch (err) {
      setError(describeApiError(err, "Quick mint failed"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <AdminPageShell
      title="Service accounts"
      description="Per-worker MCP bearer identities. Mint long-lived bearers for frontend-worker, backend-worker, wire-test-worker, future agents. Legacy frontend-worker / backend-worker names still resolve via T-336 KNOWN_WORKERS. Bearer values are shown ONCE on mint — save them immediately."
    >
      <div className="space-y-6">
        {error ? (
          <AdminCard className="border-rose-900/40 bg-rose-950/20">
            <p className="text-sm text-rose-200">{error}</p>
          </AdminCard>
        ) : null}

        {newlyMinted ? (
          <NewlyMintedBanner
            mint={newlyMinted}
            onDismiss={() => setNewlyMinted(null)}
          />
        ) : null}

        <AdminCard className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
            Create service account
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-slate-500">
                Name (alphanumeric + - _)
              </span>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="backend-worker"
                className="mt-1 w-full rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-white placeholder-slate-600"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-slate-500">
                Description (optional)
              </span>
              <input
                type="text"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="Code worker on Marc's MacBook Air"
                className="mt-1 w-full rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-white placeholder-slate-600"
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-xs uppercase tracking-wide text-slate-500">
                Bearer TTL (days, applies to Quick mint — blank = max)
              </span>
              <input
                type="number"
                min={1}
                max={365}
                value={newTtl}
                onChange={(e) =>
                  setNewTtl(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                placeholder="e.g. 90 — leave blank for max"
                className="mt-1 w-full rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-white placeholder-slate-600"
              />
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={submitQuickMint}
              disabled={creating || !newName.trim()}
              className="rounded-md bg-pink-500 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
              title="Creates the service account AND mints a default bearer in one step. The bearer appears below — copy it now (shown only once)."
            >
              {creating ? "Working…" : "Quick mint (create + bearer)"}
            </button>
            <button
              type="button"
              onClick={submitCreate}
              disabled={creating || !newName.trim()}
              className="rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800 disabled:opacity-50"
              title="Creates the service account only. Mint bearers later from the row's expand panel."
            >
              {creating ? "Working…" : "Create only"}
            </button>
          </div>
          <p className="text-xs text-slate-500">
            <strong className="text-slate-400">Quick mint</strong> creates the
            account and mints a default-name bearer in one click. The bearer
            value appears here once — copy it immediately.{" "}
            <strong className="text-slate-400">Create only</strong> stops at
            creation; mint bearers later from the row.
          </p>
        </AdminCard>

        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
            Service accounts
          </h2>
          <button
            type="button"
            onClick={refresh}
            className="rounded-md border border-slate-800 bg-slate-900/40 px-3 py-1.5 text-xs text-slate-300 hover:border-slate-700 hover:text-white"
          >
            Refresh
          </button>
        </div>

        {accounts === null ? (
          <AdminCard>
            <p className="text-sm text-slate-400">Loading…</p>
          </AdminCard>
        ) : accounts.length === 0 ? (
          <AdminCard>
            <p className="text-sm text-slate-400">
              No service accounts yet. Create one above.
            </p>
          </AdminCard>
        ) : (
          <div className="space-y-3">
            {accounts.map((sa) => (
              <ServiceAccountRow
                key={sa.id}
                sa={sa}
                onChanged={refresh}
                onMint={(m) => setNewlyMinted(m)}
              />
            ))}
          </div>
        )}
      </div>
    </AdminPageShell>
  );
}

// ─── newly-minted bearer banner ────────────────────────────────────

function NewlyMintedBanner({
  mint,
  onDismiss,
}: {
  mint: MintResponse;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <AdminCard className="border-amber-500/40 bg-amber-500/5">
      <div className="flex items-center gap-2">
        <span className="rounded-md bg-amber-500/20 px-2 py-0.5 text-xs font-mono uppercase tracking-wide text-amber-300">
          ★ One-time
        </span>
        <h3 className="font-semibold text-white">Bearer minted — save it now</h3>
        <button
          type="button"
          onClick={onDismiss}
          className="ml-auto text-xs text-slate-400 hover:text-slate-200"
        >
          Dismiss
        </button>
      </div>
      <p className="mt-2 text-xs text-amber-200/80">{mint.notice}</p>
      <div className="mt-3 grid gap-2 text-xs">
        <div>
          <div className="uppercase tracking-wide text-slate-500">JTI</div>
          <code className="text-slate-300">{mint.jti}</code>
        </div>
        <div>
          <div className="uppercase tracking-wide text-slate-500">
            Expires at
          </div>
          <code className="text-slate-300">{mint.expires_at}</code>
        </div>
      </div>
      <div className="mt-3">
        <div className="text-xs uppercase tracking-wide text-slate-500">
          Bearer (JWT)
        </div>
        <textarea
          readOnly
          value={mint.bearer}
          rows={4}
          className="mt-1 w-full rounded-md border border-amber-500/30 bg-slate-950 px-3 py-2 font-mono text-xs text-amber-100"
          onFocus={(e) => e.currentTarget.select()}
        />
        <button
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(mint.bearer).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            });
          }}
          className="mt-2 rounded-md bg-amber-500/20 px-3 py-1.5 text-xs font-medium text-amber-200 hover:bg-amber-500/30"
        >
          {copied ? "Copied!" : "Copy bearer to clipboard"}
        </button>
      </div>
    </AdminCard>
  );
}

// ─── per-account row + detail ──────────────────────────────────────

function ServiceAccountRow({
  sa,
  onChanged,
  onMint,
}: {
  sa: ServiceAccount;
  onChanged: () => void;
  onMint: (m: MintResponse) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState<ServiceAccountDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mintTtl, setMintTtl] = useState<number | "">("");
  const [mintName, setMintName] = useState("");

  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    apiFetch<ServiceAccountDetail>(`/api/admin/service-accounts/${sa.id}`)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(describeApiError(err, "Detail load failed"));
      });
    return () => {
      cancelled = true;
    };
  }, [expanded, sa.id]);

  const mintBearer = async () => {
    setBusy(true);
    setError(null);
    try {
      const m = await apiFetch<MintResponse>(
        `/api/admin/service-accounts/${sa.id}/bearers`,
        {
          method: "POST",
          body: JSON.stringify({
            name: mintName.trim() || null,
            ttl_days: typeof mintTtl === "number" ? mintTtl : null,
          }),
        },
      );
      setMintName("");
      setMintTtl("");
      onMint(m);
      onChanged();
      // Refresh local detail
      const d = await apiFetch<ServiceAccountDetail>(
        `/api/admin/service-accounts/${sa.id}`,
      );
      setDetail(d);
    } catch (err) {
      setError(describeApiError(err, "Mint failed"));
    } finally {
      setBusy(false);
    }
  };

  const revokeBearer = async (jti: string) => {
    if (!confirm(`Revoke bearer ${jti.slice(0, 8)}…? Cannot be undone.`)) return;
    setBusy(true);
    try {
      await apiFetch<void>(
        `/api/admin/service-accounts/${sa.id}/bearers/${jti}`,
        { method: "DELETE" },
      );
      const d = await apiFetch<ServiceAccountDetail>(
        `/api/admin/service-accounts/${sa.id}`,
      );
      setDetail(d);
      onChanged();
    } catch (err) {
      setError(describeApiError(err, "Revoke failed"));
    } finally {
      setBusy(false);
    }
  };

  const revokeAccount = async () => {
    if (
      !confirm(
        `Revoke service account "${sa.name}"? Marks inactive + revokes all bearers. Cannot be undone.`,
      )
    )
      return;
    setBusy(true);
    try {
      await apiFetch<void>(`/api/admin/service-accounts/${sa.id}`, {
        method: "DELETE",
      });
      onChanged();
    } catch (err) {
      setError(describeApiError(err, "Revoke account failed"));
      setBusy(false);
    }
  };

  return (
    <AdminCard className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-mono uppercase tracking-wide ${
            sa.is_active
              ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-300"
              : "border-rose-500/30 bg-rose-500/15 text-rose-300"
          }`}
        >
          {sa.is_active ? "Active" : "Revoked"}
        </span>
        <span className="font-semibold text-white">{sa.name}</span>
        {sa.description ? (
          <span className="text-xs text-slate-400">— {sa.description}</span>
        ) : null}
        <span className="ml-auto text-xs text-slate-500">
          {sa.bearer_count} bearer{sa.bearer_count === 1 ? "" : "s"} ·{" "}
          {sa.last_bearer_used_at
            ? `last used ${formatRelativeTime(sa.last_bearer_used_at)}`
            : "never used"}
        </span>
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="rounded-md border border-slate-800 bg-slate-900/40 px-2 py-1 text-xs text-slate-300 hover:border-slate-700 hover:text-white"
        >
          {expanded ? "Collapse" : "Expand"}
        </button>
      </div>

      {expanded ? (
        <div className="space-y-4 border-t border-slate-800 pt-4">
          {error ? (
            <p className="text-xs text-rose-300">{error}</p>
          ) : null}
          {detail === null ? (
            <p className="text-xs text-slate-400">Loading detail…</p>
          ) : (
            <>
              {sa.is_active ? (
                <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                    Mint a new bearer
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <input
                      type="text"
                      value={mintName}
                      onChange={(e) => setMintName(e.target.value)}
                      placeholder="bearer name (optional, e.g., 'main')"
                      className="w-full rounded-md border border-slate-800 bg-slate-950 px-3 py-1.5 text-xs text-white"
                    />
                    <input
                      type="number"
                      min={1}
                      max={365}
                      value={mintTtl}
                      onChange={(e) =>
                        setMintTtl(
                          e.target.value === "" ? "" : Number(e.target.value),
                        )
                      }
                      placeholder="TTL days (default 90, max 365)"
                      className="w-full rounded-md border border-slate-800 bg-slate-950 px-3 py-1.5 text-xs text-white"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={mintBearer}
                    disabled={busy}
                    className="mt-2 rounded-md bg-pink-500 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                  >
                    {busy ? "Minting…" : "Mint bearer"}
                  </button>
                </div>
              ) : null}

              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                  Bearers ({detail.bearers.length})
                </div>
                {detail.bearers.length === 0 ? (
                  <p className="text-xs text-slate-400">No bearers yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {detail.bearers.map((b) => (
                      <BearerRow
                        key={b.id}
                        bearer={b}
                        canRevoke={sa.is_active && b.revoked_at === null}
                        onRevoke={() => revokeBearer(b.jti)}
                      />
                    ))}
                  </ul>
                )}
              </div>

              {sa.is_active ? (
                <div className="border-t border-slate-800 pt-3">
                  <button
                    type="button"
                    onClick={revokeAccount}
                    disabled={busy}
                    className="rounded-md border border-rose-700 px-3 py-1.5 text-xs font-medium text-rose-300 hover:bg-rose-950/40 disabled:opacity-50"
                  >
                    Revoke service account (soft delete)
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </AdminCard>
  );
}

function BearerRow({
  bearer,
  canRevoke,
  onRevoke,
}: {
  bearer: Bearer;
  canRevoke: boolean;
  onRevoke: () => void;
}) {
  const status = useMemo(() => {
    if (bearer.revoked_at) return { label: "Revoked", color: "rose" };
    const expiresAt = new Date(bearer.expires_at).getTime();
    const now = Date.now();
    if (expiresAt < now) return { label: "Expired", color: "rose" };
    // < 14 days from expiry — amber warning so operators rotate
    // before downstream daemons start failing.
    const daysUntilExpiry = (expiresAt - now) / (1000 * 60 * 60 * 24);
    if (daysUntilExpiry < 14)
      return { label: "Expiring soon", color: "amber" };
    return { label: "Active", color: "emerald" };
  }, [bearer.revoked_at, bearer.expires_at]);
  return (
    <li className="rounded-md border border-slate-800 bg-slate-950/40 p-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center rounded border px-1.5 py-0.5 font-mono uppercase tracking-wide ${
            status.color === "emerald"
              ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-300"
              : status.color === "amber"
                ? "border-amber-500/30 bg-amber-500/15 text-amber-300"
                : "border-rose-500/30 bg-rose-500/15 text-rose-300"
          }`}
        >
          {status.label}
        </span>
        <code className="text-slate-400">{bearer.jti.slice(0, 8)}…</code>
        {bearer.name ? (
          <span className="text-slate-300">{bearer.name}</span>
        ) : (
          <span className="text-slate-500">(unnamed)</span>
        )}
        <span className="ml-auto text-slate-500">
          {bearer.last_used_at
            ? `last used ${formatRelativeTime(bearer.last_used_at)}`
            : "never used"}
          {" · expires "}
          {formatRelativeTime(bearer.expires_at)}
        </span>
        {canRevoke ? (
          <button
            type="button"
            onClick={onRevoke}
            className="rounded border border-rose-700 px-2 py-0.5 text-rose-300 hover:bg-rose-950/40"
          >
            Revoke
          </button>
        ) : null}
      </div>
    </li>
  );
}

function formatRelativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const diff = t - Date.now();
  const absSec = Math.floor(Math.abs(diff) / 1000);
  const future = diff > 0;
  if (absSec < 60) return future ? "in <1m" : "<1m ago";
  const min = Math.floor(absSec / 60);
  if (min < 60) return future ? `in ${min}m` : `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return future ? `in ${hr}h` : `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 365) return future ? `in ${day}d` : `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}
